import React, { useState, useEffect, useRef } from "react";
import api from "../api/axios";

type Raid = {
  _id: string;
  initiator: string;
  participants: string[];
  status: "waiting" | "in_progress" | "resolved";
  result?: "won" | "lost" | "failed";
};

export default function RaidPage() {
  const [myRaid, setMyRaid]       = useState<Raid | null>(null);
  const [openRaids, setOpenRaids] = useState<Raid[]>([]);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  // fetch waiting raids
  const fetchRaids = () =>
    api.get<Raid[]>("/raid")
      .then(r => setOpenRaids(r.data))
      .catch(console.error);

  // fetch my raid status
  const fetchMyRaid = (id: string) =>
    api.get<Raid>(`/raid/${id}`)
      .then(r => setMyRaid(r.data))
      .catch(console.error);

  useEffect(() => {
    fetchRaids();
    return () => clearInterval(pollRef.current!);
  }, []);

  // create a raid
  const start = async () => {
    const { data } = await api.post<Raid>("/raid/start", {});
    setMyRaid(data);
    setOpenRaids(rs => rs.filter(r => r._id !== data._id));
    // start polling my raid
    pollRef.current = setInterval(() => fetchMyRaid(data._id), 3000);
  };

  // join someone else's raid
  const join = async (id: string) => {
    const { data } = await api.post<Raid>("/raid/join", { raidId: id });
    setMyRaid(data);
    clearInterval(pollRef.current!);
    pollRef.current = setInterval(() => fetchMyRaid(data._id), 3000);
  };

  // when raid resolves, stop polling
  useEffect(() => {
    if (myRaid?.status === "resolved") {
      clearInterval(pollRef.current!);
      alert(`Raid ${myRaid.result === "failed" ? "failed" : "completed!"}`);
    }
  }, [myRaid]);

  return (
    <div style={{ maxWidth:600, margin:"2rem auto" }}>
      <h2>Raid Arena</h2>

      {!myRaid ? (
        <>
          <button onClick={start}>Start a Raid</button>
          <h3>Open Raids</h3>
          {openRaids.length ? openRaids.map(r => (
            <div key={r._id} style={{ padding:12, border:"1px solid #ccc", marginBottom:8 }}>
              <p>Host: {r.initiator}</p>
              <button onClick={()=>join(r._id)}>Join</button>
            </div>
          )) : <p>No raids waiting.</p>}
        </>
      ) : (
        <div>
          <h3>Your Raid</h3>
          <p>Status: {myRaid.status}</p>
          <p>Host: {myRaid.initiator}</p>
          <p>Participants: {myRaid.participants.join(", ")}</p>
          {myRaid.status === "waiting" && <p>Waiting for someone to join…</p>}
          {myRaid.status === "in_progress" && <p>Raid in progress! Good luck!</p>}
          {myRaid.status === "resolved" && (
            <p>
              {myRaid.result === "failed"
                ? "No one joined. Raid failed."
                : myRaid.result === "won"
                  ? "Your party won!"
                  : "Your party lost!"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
